import { memo, useRef } from 'react';
import { motion } from 'framer-motion';
import { useApp } from '../../../contexts/AppContext';
import { Check, Sparkles, RefreshCw, Plus } from 'lucide-react';
import { Card, CardHeader } from '../../ui/Card';
import { microInteractions } from '../../../utils/animations';
import { haptics } from '../../../utils/haptics';
import DispatchProgressOverlay from './DispatchProgressOverlay';
import AutoSyncDesignToggle from './AutoSyncDesignToggle';

/**
 * Design Card Component
 * Per-station dispatch progress is driven by AppContext.dispatchProgress
 * (populated from inbound `dispatch_progress` Ably events).
 *
 * `mode="add"` is the Service Playlist picker: clicking the card or its Add
 * button calls `onAdd(design, thumbnailElement)`; selection and dispatch
 * overlays are not rendered.
 */
const DesignCard = memo(function DesignCard({
  design,
  onRetryStation,
  mode = 'select',
  playlistCount = 0,
  onAdd,
  addDisabledReason = null,
}) {
  const { state, toggleDesignSelection } = useApp();
  const { selectedDesigns, completedDesigns, dispatchProgress, linkedAgents } = state;
  const thumbnailRef = useRef(null);

  const designId = design.design_id || design.id;
  const isAddMode = mode === 'add';
  const isSelected = !isAddMode && selectedDesigns.includes(designId);
  const isCompleted = !isAddMode && completedDesigns.includes(designId);

  const progressByAgent = dispatchProgress[designId] || {};
  const stationEntries = Object.values(progressByAgent);
  // Treat 'done' entries as still occupying the card: the dispatchOne owner
  // clears them explicitly after the full pipeline (Send, or Send→Load chain)
  // resolves. This prevents the empty-card flash between chained workflows.
  const hasActive = stationEntries.some(e => e.stage !== 'failed');
  const hasFailed = stationEntries.some(e => e.stage === 'failed');
  const isDispatching = !isAddMode && stationEntries.length > 0 && (hasActive || hasFailed);
  const title = design.title || 'Untitled Design';

  const handleSelect = () => {
    if (isAddMode) {
      if (!addDisabledReason) onAdd?.(design, thumbnailRef.current);
      return;
    }
    if (isDispatching) return;
    haptics.light();
    toggleDesignSelection(designId);
  };

  const handleAddClick = (event) => {
    event.stopPropagation();
    if (!addDisabledReason) onAdd?.(design, thumbnailRef.current);
  };

  const cursorClass = isAddMode
    ? (addDisabledReason ? 'cursor-not-allowed' : 'cursor-pointer')
    : (isDispatching ? 'cursor-not-allowed opacity-90' : 'cursor-pointer');

  return (
    <Card
      interactive
      variant="elevated"
      onClick={handleSelect}
      data-design-id={designId}
      className={`overflow-hidden transition-shadow duration-300 dark:hover:shadow-glow-card ${cursorClass} ${isSelected ? 'ring-2 ring-primary-500 shadow-glow' : ''}`}
    >
      {/* Thumbnail */}
      <motion.div
        ref={thumbnailRef}
        className="relative overflow-hidden bg-gray-100 dark:bg-elevated-dark-1"
        style={{ paddingBottom: '75%' }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 400, damping: 17 }}
      >
        {design.thumbnail_url ? (
          <img
            src={design.thumbnail_url}
            alt={design.title}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <svg className="w-16 h-16 text-gray-300 dark:text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
        
        {/* Dispatch Progress Overlay (per-station, driven by Ably events) */}
        {isDispatching && (
          <DispatchProgressOverlay
            progressByAgent={progressByAgent}
            agents={linkedAgents}
            onRetry={(agentId) => onRetryStation?.(design, agentId)}
          />
        )}

        {/* Success Overlay */}
        {isCompleted && !isDispatching && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, type: 'spring' }}
            className="absolute inset-0 bg-gradient-to-br from-green-500 to-green-600 flex flex-col items-center justify-center gap-2 z-10"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ 
                delay: 0.1, 
                duration: 0.6, 
                type: 'spring',
                stiffness: 200,
                damping: 10
              }}
              className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg"
            >
              <Check className="w-9 h-9 text-green-600" strokeWidth={3} />
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-white font-bold text-lg drop-shadow-lg"
            >
              Sent!
            </motion.p>
          </motion.div>
        )}

        {/* Selection Indicator */}
        {isSelected && !isCompleted && !isDispatching && (
          <motion.div
            {...microInteractions.checkboxPop}
            className="absolute top-3 right-3 bg-primary-600 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-glow z-20"
          >
            <Check className="w-5 h-5" strokeWidth={3} />
          </motion.div>
        )}

        {/* New / Updated Badge */}
        {!isCompleted && !isDispatching && (design.is_new || design.is_updated) && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            className={`absolute top-3 ${isAddMode ? 'right-3' : 'left-3'} flex items-center gap-1 px-2.5 py-1 squircle-sm shadow-soft z-20 ${
              design.is_new
                ? 'bg-amber-500 text-white'
                : 'bg-blue-500 text-white'
            }`}
          >
            {design.is_new ? (
              <Sparkles className="w-3 h-3" strokeWidth={2.5} />
            ) : (
              <RefreshCw className="w-3 h-3" strokeWidth={2.5} />
            )}
            <span className="text-[11px] font-semibold uppercase tracking-wide">
              {design.is_new ? 'New' : 'Updated'}
            </span>
          </motion.div>
        )}

        {isAddMode && playlistCount > 0 && (
          <motion.div
            key={playlistCount}
            {...microInteractions.checkboxPop}
            data-testid="design-in-playlist-badge"
            className="absolute top-3 left-3 flex items-center gap-1 px-2.5 py-1 squircle-sm shadow-soft z-20 bg-primary-600 text-white"
          >
            <Check className="w-3 h-3" strokeWidth={3} />
            <span className="text-[11px] font-semibold">In playlist ×{playlistCount}</span>
          </motion.div>
        )}

        {isAddMode && (
          <button
            type="button"
            onClick={handleAddClick}
            disabled={Boolean(addDisabledReason)}
            aria-label={`Add ${title} to the playlist`}
            title={addDisabledReason || 'Add to playlist'}
            data-testid="design-add-button"
            data-design-id={designId}
            className="group/add absolute bottom-1.5 right-1.5 z-20 touch-target flex items-center justify-center rounded-full focus-visible:outline-none disabled:cursor-not-allowed"
          >
            <span className="w-9 h-9 rounded-full flex items-center justify-center bg-white/90 dark:bg-elevated-dark-1/90 shadow-soft text-primary-700 dark:text-primary-300 transition-transform group-hover/add:scale-105 group-active/add:scale-95 group-disabled/add:opacity-50 group-focus-visible/add:ring-2 group-focus-visible/add:ring-primary-500 group-focus-visible/add:ring-offset-2 dark:group-focus-visible/add:ring-offset-elevated-dark-1">
              <Plus className="w-5 h-5" strokeWidth={2.5} />
            </span>
          </button>
        )}

        {/* Gradient Overlay on Hover */}
        {!isCompleted && !isDispatching && (
          <motion.div
            className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity pointer-events-none"
          />
        )}
      </motion.div>

      {/* Content */}
      <CardHeader>
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate text-body">
          {title}
        </h3>
        {design.page_count && (
          <p className="text-caption text-gray-500 dark:text-gray-400 mt-1">
            {design.page_count} page{design.page_count !== 1 ? 's' : ''}
          </p>
        )}
        {!isAddMode && <AutoSyncDesignToggle designId={designId} />}
      </CardHeader>
    </Card>
  );
});

export default DesignCard;
